/**
 * Televendas (redesign admin, Fase 3): o operador monta o pedido de um cliente
 * atendido por telefone/WhatsApp e o registra como venda de canal. Regras e
 * conversões em `orders/televendas.ts`; o servidor valida de novo.
 *
 * Link de pagamento (C3): depois de registrar a venda o pedido nasce em "Aguardando
 * pagamento" e o painel `PaymentLinkPanel` gera o link de cobrança (Mercado Pago com
 * credencial; simulador rotulado como fictício sem ela). O link não confirma o pagamento.
 */
import React, { createElement as h, useMemo, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import type { CatalogDataset } from "../catalog/dataset.js";
import type { CreatePlatformOrderInput } from "../api/platform-client.js";
import type { PlatformUnifiedOrder } from "../api/types.js";
import { formatBrl } from "../format.js";
import {
  TELEVENDAS_ORIGINS,
  addToCart,
  buildOrderInput,
  cartSubtotalCents,
  overStockLines,
  parseBrlToCents,
  sellableProducts,
  setQuantity,
  type TelevendasField,
  type TelevendasForm,
  type TelevendasOrigin
} from "../orders/televendas.js";
import { ORDER_ORIGIN_LABELS } from "../orders/state-machine.js";
import { useCreatePlatformOrder, useSalesOrigins } from "../queries.js";
import { Button, Card, CardHeader, Field, PageHeader, controlClass } from "../ui/index.js";
import type { SalesOriginOption } from "../orders/acquisition.js";
import { PaymentLinkPanel } from "../components/PaymentLinkPanel.js";
import { useApiClient } from "../api/ApiClientContext.js";
import type { PaymentLink } from "../contracts/platform-extras.js";

export interface TelevendasViewProps {
  catalog: CatalogDataset;
  /** Registra o pedido; rejeitar leva a mensagem do servidor à tela. */
  onSubmit?: (input: CreatePlatformOrderInput) => Promise<PlatformUnifiedOrder>;
  initialForm?: Partial<TelevendasForm>;
  /** Origens de aquisição cadastradas (0062). Ausente = a tela não pede origem (rota não publicada). */
  origins?: readonly SalesOriginOption[];
  /** Nomes sugeridos para "Responsável" (equipe); o campo aceita nome livre. */
  responsibleSuggestions?: readonly string[];
  /** C3: liga o painel de link de pagamento ao contrato HTTP. Ausente = a tela não oferece o link. */
  paymentLinks?: { load: (orderId: string) => Promise<PaymentLink[]>; create: (orderId: string) => Promise<PaymentLink> };
}

const EMPTY_FORM: TelevendasForm = {
  origin: "whatsapp",
  externalId: "",
  name: "",
  email: "",
  phone: "",
  shipping: "",
  cart: []
};

export function TelevendasView({ catalog, onSubmit, initialForm, paymentLinks, origins, responsibleSuggestions }: TelevendasViewProps): ReactElement {
  const activeOrigins = (origins ?? []).filter((origin) => origin.active);
  const [form, setForm] = useState<TelevendasForm>({ ...EMPTY_FORM, ...initialForm });
  const [query, setQuery] = useState("");
  const [errors, setErrors] = useState<Partial<Record<TelevendasField, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [created, setCreated] = useState<PlatformUnifiedOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const header = h(PageHeader, {
    title: "Televendas",
    description: "Monte o pedido do cliente e registre a venda no canal."
  });

  if (catalog.sources.platformCatalog === "awaiting_contract") {
    return h(
      "div",
      { className: "page page--televendas flex flex-col gap-6", "data-page": "televendas" },
      header,
      h(StatusPanel, {
        kind: "awaiting_source",
        title: `Catálogo vendável ${AWAITING_CONTRACT_LABEL}`,
        detail: "Sem o catálogo da Plataforma não há produto e preço para montar o pedido."
      })
    );
  }

  const set = <K extends keyof TelevendasForm>(key: K, value: TelevendasForm[K]): void => setForm((prev) => ({ ...prev, [key]: value }));
  const subtotal = cartSubtotalCents(form.cart);
  const shippingCents = parseBrlToCents(form.shipping);
  const stockWarnings = overStockLines(form.cart);
  const matches = sellableProducts(catalog.products, query).slice(0, 6);

  async function submit(): Promise<void> {
    setServerError(null);
    const result = buildOrderInput(form, { requireAcquisitionOrigin: activeOrigins.length > 0 });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    if (!onSubmit) return;
    setSubmitting(true);
    try {
      setCreated(await onSubmit(result.input));
      setForm(EMPTY_FORM);
    } catch (error) {
      setServerError(errorDetail(error));
    } finally {
      setSubmitting(false);
    }
  }

  return h(
    "div",
    { className: "page page--televendas flex flex-col gap-6", "data-page": "televendas" },
    header,
    created
      ? h(
          "div",
          { role: "status", className: "rounded-panel border border-success bg-success-soft p-4 text-sm text-ink", "data-televendas": "criado" },
          `Venda registrada (${created.externalId}). `,
          h(Link, { to: `/pedidos/${created.id}`, className: "font-bold text-brand-text" }, "Abrir pedido")
        )
      : null,
    created && paymentLinks ? h(PaymentLinkPanel, { orderId: created.id, onLoad: paymentLinks.load, onCreate: paymentLinks.create }) : null,
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 lg:grid-cols-2" },
      h(
        Card,
        { "data-panel": "cliente" },
        h(CardHeader, { title: "Cliente e canal" }),
        h(
          "div",
          { className: "flex flex-col gap-3" },
          h(Field, {
            label: "Canal da venda",
            children: (ids) =>
              h(
                "select",
                {
                  id: ids.id,
                  className: controlClass,
                  value: form.origin,
                  "data-televendas-field": "origin",
                  onChange: (e: { target: { value: string } }) => set("origin", e.target.value as TelevendasOrigin)
                },
                TELEVENDAS_ORIGINS.map((origin) => h("option", { key: origin, value: origin }, ORDER_ORIGIN_LABELS[origin]))
              )
          }),
          activeOrigins.length > 0
            ? h(Field, {
                label: "Origem do contato",
                error: errors.acquisitionOrigin,
                hint: "De onde este cliente chegou: anúncio, indicação, site...",
                children: (ids) =>
                  h(
                    "select",
                    {
                      id: ids.id,
                      className: controlClass,
                      value: form.acquisitionOrigin ?? "",
                      "aria-invalid": ids.invalid,
                      "aria-describedby": ids.describedBy,
                      "data-televendas-field": "acquisitionOrigin",
                      onChange: (e: { target: { value: string } }) => set("acquisitionOrigin", e.target.value)
                    },
                    h("option", { value: "" }, "Selecione…"),
                    activeOrigins.map((origin) => h("option", { key: origin.slug, value: origin.slug }, origin.label))
                  )
              })
            : null,
          activeOrigins.length > 0
            ? h(Field, {
                label: "Estratégia ou campanha (opcional)",
                hint: "Ex.: Black Friday, Reels de lançamento.",
                children: (ids) =>
                  h("input", {
                    id: ids.id,
                    className: controlClass,
                    value: form.acquisitionStrategy ?? "",
                    "data-televendas-field": "acquisitionStrategy",
                    onChange: (e: { target: { value: string } }) => set("acquisitionStrategy", e.target.value)
                  })
              })
            : null,
          activeOrigins.length > 0
            ? h(Field, {
                label: "Responsável pela venda (opcional)",
                children: (ids) =>
                  h(
                    React.Fragment,
                    null,
                    h("input", {
                      id: ids.id,
                      className: controlClass,
                      list: "televendas-responsaveis",
                      value: form.responsibleName ?? "",
                      "data-televendas-field": "responsibleName",
                      onChange: (e: { target: { value: string } }) => set("responsibleName", e.target.value)
                    }),
                    h("datalist", { id: "televendas-responsaveis" }, (responsibleSuggestions ?? []).map((name) => h("option", { key: name, value: name })))
                  )
              })
            : null,
          textField("Identificador da venda", "externalId", form.externalId, set, errors.externalId, "Ex.: número do atendimento ou protocolo."),
          textField("Nome do cliente", "name", form.name, set, errors.name),
          textField("E-mail", "email", form.email, set, errors.email),
          textField("Telefone (com DDD)", "phone", form.phone, set, errors.phone),
          textField("Frete combinado (R$)", "shipping", form.shipping, set, errors.shipping, "Use 0 se não houver frete.")
        )
      ),
      h(
        Card,
        { "data-panel": "itens" },
        h(CardHeader, { title: "Itens do pedido" }),
        h(
          "div",
          { className: "flex flex-col gap-3" },
          h(Field, {
            label: "Buscar produto (nome ou SKU)",
            children: (ids) =>
              h("input", {
                id: ids.id,
                type: "search",
                className: controlClass,
                value: query,
                "data-televendas-field": "busca",
                onChange: (e: { target: { value: string } }) => setQuery(e.target.value)
              })
          }),
          h(
            "ul",
            { className: "m-0 flex list-none flex-col divide-y divide-border p-0", "aria-label": "Produtos vendáveis" },
            matches.map((product) =>
              h(
                "li",
                { key: product.productId, className: "flex items-center justify-between gap-2 py-2 text-sm", "data-product": product.productId },
                h("span", { className: "min-w-0 flex-1 truncate" }, `${product.name} · ${product.sku}`),
                h("span", { className: "tabular-nums text-muted" }, formatBrl(product.sellableOffer?.priceCents ?? 0)),
                h(Button, { size: "sm", onClick: () => set("cart", addToCart(form.cart, product)) }, "Adicionar")
              )
            )
          ),
          errors.cart ? h("p", { className: "text-xs font-semibold text-danger" }, errors.cart) : null,
          form.cart.length
            ? h(
                "table",
                { className: "w-full text-left text-sm", "data-table": "carrinho" },
                h(
                  "tbody",
                  null,
                  form.cart.map((line) =>
                    h(
                      "tr",
                      { key: line.offerId, "data-cart-line": line.offerId, className: "border-t border-border" },
                      h("td", { className: "py-2" }, line.name),
                      h(
                        "td",
                        { className: "py-2" },
                        h("input", {
                          type: "number",
                          min: 0,
                          "aria-label": `Quantidade de ${line.name}`,
                          className: controlClass + " w-20",
                          value: line.quantity,
                          onChange: (e: { target: { value: string } }) => set("cart", setQuantity(form.cart, line.offerId, Number(e.target.value)))
                        })
                      ),
                      h("td", { className: "py-2 text-right tabular-nums" }, formatBrl(line.unitPriceCents * line.quantity))
                    )
                  )
                )
              )
            : null,
          stockWarnings.length
            ? h(
                "p",
                { className: "text-xs font-semibold text-warning", "data-televendas": "estoque" },
                `Acima do estoque: ${stockWarnings.map((line) => line.name).join(", ")}.`
              )
            : null,
          h(
            "dl",
            { className: "m-0 flex flex-col gap-1 border-t border-border pt-3 text-sm" },
            h("div", { className: "flex justify-between" }, h("dt", null, "Subtotal"), h("dd", { className: "m-0 tabular-nums", "data-total": "subtotal" }, formatBrl(subtotal))),
            h("div", { className: "flex justify-between" }, h("dt", null, "Frete"), h("dd", { className: "m-0 tabular-nums" }, shippingCents === null ? "—" : formatBrl(shippingCents))),
            h(
              "div",
              { className: "flex justify-between font-extrabold text-ink" },
              h("dt", null, "Total"),
              h("dd", { className: "m-0 tabular-nums", "data-total": "total" }, shippingCents === null ? "—" : formatBrl(subtotal + shippingCents))
            )
          )
        )
      )
    ),
    serverError ? h("p", { role: "alert", className: "text-sm font-semibold text-danger", "data-televendas": "erro" }, serverError) : null,
    h(
      "div",
      { className: "flex flex-wrap items-center gap-3" },
      h(Button, { variant: "primary", disabled: submitting, onClick: () => void submit(), "data-televendas-action": "registrar" }, submitting ? "Registrando…" : "Registrar venda"),
      h(
        "p",
        { className: "text-xs text-muted", "data-televendas": "link-pagamento" },
        paymentLinks
          ? 'O pedido nasce em "Aguardando pagamento"; depois de registrar a venda, gere o link de pagamento aqui mesmo.'
          : `Link de pagamento ${AWAITING_CONTRACT_LABEL}: o pedido nasce em "Aguardando pagamento".`
      )
    )
  );
}

function textField(
  label: string,
  key: "externalId" | "name" | "email" | "phone" | "shipping",
  value: string,
  set: <K extends keyof TelevendasForm>(key: K, value: TelevendasForm[K]) => void,
  error: string | undefined,
  hint?: string
): ReactElement {
  return h(Field, {
    label,
    error,
    hint,
    children: (ids) =>
      h("input", {
        id: ids.id,
        className: controlClass,
        value,
        "aria-invalid": ids.invalid,
        "aria-describedby": ids.describedBy,
        "data-televendas-field": key,
        onChange: (e: { target: { value: string } }) => set(key, e.target.value)
      })
  });
}

export function Televendas(): ReactElement {
  const create = useCreatePlatformOrder();
  const client = useApiClient();
  // Cadastro de origens: se a rota não existir neste ambiente a tela segue sem pedir origem.
  const origins = useSalesOrigins();
  // Funções estáveis entre renders: o painel recarrega os links quando `onLoad` muda.
  const paymentLinks = useMemo(
    () => ({ load: (orderId: string) => client.listPaymentLinks(orderId), create: (orderId: string) => client.createPaymentLink(orderId) }),
    [client]
  );
  return h(CatalogBoundary, {
    children: (catalog: CatalogDataset) => h(TelevendasView, { catalog, onSubmit: (input) => create.mutateAsync(input), paymentLinks, origins: origins.data })
  });
}
